/**
 * El aviso de calidad del cierre: qué categorías avisar y con qué motivo por foto.
 *
 * La calidad no bloquea el cierre (ver `RequiredPhotos.ts`); el asegurado la ve como advertencia y
 * elige «Aprobar» o «Corregir». Por eso el aviso tiene que decir QUÉ detectó la IA en cada foto: un
 * conteo por categoría le pide aprobar a ciegas.
 */

export interface WarnableFile {
  analysisResult?: { status?: string; messages?: unknown };
}

export interface WarnableSection {
  photoId: number;
  title: string;
  files: WarnableFile[];
}

export interface QualityWarning {
  photoId: number;
  title: string;
  /** Una entrada por foto en error, lista para mostrar. */
  photoReasons: string[];
}

export const QUALITY_WARNING_FALLBACK = 'La IA marcó esta foto, pero no indicó el motivo.';

/**
 * Categorías con al menos una foto en `error` y ninguna en `success`, con solo sus fotos en error.
 * Una foto buena en la categoría basta para no avisar: el paso ya quedó cubierto.
 */
export function selectSectionsWithQualityErrors<S extends WarnableSection>(sections: S[]): S[] {
  return sections
    .filter(
      (section) =>
        section.files.some((file) => file.analysisResult?.status === 'error') &&
        !section.files.some((file) => file.analysisResult?.status === 'success')
    )
    .map((section) => ({
      ...section,
      files: section.files.filter((file) => file.analysisResult?.status === 'error'),
    }));
}

/** Los motivos de una foto sin vacíos ni repetidos; si la IA no dio ninguno, el texto de respaldo. */
export function describePhotoReasons(file: WarnableFile): string {
  const raw = file.analysisResult?.messages;
  const messages = Array.isArray(raw) ? raw : [];
  const visible = messages
    .filter((message): message is string => typeof message === 'string')
    .map((message) => message.trim())
    .filter((message) => message !== '');
  const unique = [...new Set(visible)];
  return unique.length > 0 ? unique.join(', ') : QUALITY_WARNING_FALLBACK;
}

export function describeQualityWarnings(sections: WarnableSection[]): QualityWarning[] {
  return selectSectionsWithQualityErrors(sections).map((section) => ({
    photoId: section.photoId,
    title: section.title,
    photoReasons: section.files.map(describePhotoReasons),
  }));
}
